import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CartSheet } from "@/components/CartSheet";
import { ProductCard } from "@/components/ProductCard";

const SORTS = [
    { id: "relevancia", label: "Relevancia" },
    { id: "precio_asc", label: "Precio ↑" },
    { id: "precio_desc", label: "Precio ↓" },
    { id: "nombre", label: "Nombre A-Z" },
];

export default function Catalog() {
    const [params, setParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState(params.get("search") || "");
    const [showFilters, setShowFilters] = useState(false);

    const categoria = params.get("categoria") || "";
    const marca = params.get("marca") || "";
    const search = params.get("search") || "";
    const sort = params.get("sort") || "relevancia";
    const page = parseInt(params.get("page") || "1", 10);

    useEffect(() => {
        const loadCats = (attempt = 0) =>
            api.get("/products/categories")
                .then((r) => setCategories(r.data))
                .catch(() => { if (attempt < 5) setTimeout(() => loadCats(attempt + 1), 2000); });
        loadCats();
    }, []);

    const fetchProducts = useCallback((attempt = 0) => {
        setLoading(true);
        const p = { page, limit: 24, sort };
        if (categoria) p.categoria = categoria;
        if (marca) p.marca = marca;
        if (search) p.search = search;
        api.get("/products", { params: p })
            .then((r) => { setData(r.data); setLoading(false); })
            .catch(() => {
                // El backend puede estar reiniciándose: reintentar antes de mostrar vacío
                if (attempt < 6) setTimeout(() => fetchProducts(attempt + 1), 2000);
                else setLoading(false);
            });
    }, [categoria, marca, search, sort, page]);

    useEffect(() => {
        fetchProducts();
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [fetchProducts]);

    const update = (patch) => {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([k, v]) => {
            if (v) next.set(k, v);
            else next.delete(k);
        });
        if (!("page" in patch)) next.set("page", "1");
        setParams(next);
    };

    const submitSearch = (e) => {
        e.preventDefault();
        update({ search: searchInput });
        setShowFilters(false);
    };

    const activeTitle = search ? `“${search}”` : categoria || marca || "Todo el catálogo";

    const FilterPanel = () => (
        <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-4">Categorías</h4>
            <ul className="space-y-1 max-h-[460px] overflow-y-auto pr-1">
                <li>
                    <button
                        onClick={() => { update({ categoria: "" }); setShowFilters(false); }}
                        className={`text-left text-sm py-1.5 rounded-md px-2 w-full hover:bg-secondary transition-colors ${!categoria ? "text-verde font-semibold bg-secondary" : "text-mut"}`}
                    >
                        Todas
                    </button>
                </li>
                {categories.map((c) => (
                    <li key={c.nombre}>
                        <button
                            data-testid={`filter-cat-${c.nombre}`}
                            onClick={() => { update({ categoria: c.nombre }); setShowFilters(false); }}
                            className={`text-left text-sm py-1.5 rounded-md px-2 flex justify-between w-full gap-2 hover:bg-secondary transition-colors ${categoria === c.nombre ? "text-verde font-semibold bg-secondary" : "text-mut"}`}
                        >
                            <span className="line-clamp-1">{c.nombre}</span>
                            <span className="text-[11px] opacity-60 shrink-0">{c.count}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <div className="bg-crema min-h-screen">
            <Navbar />
            <CartSheet />

            <div className="pt-16 md:pt-20">
                {/* Header */}
                <div className="hero-gradient text-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-madera">Tienda · {data.total} productos</span>
                        <h1 className="font-display font-extrabold text-4xl md:text-6xl mt-2 tracking-tight capitalize">{activeTitle}</h1>
                        <form onSubmit={submitSearch} className="mt-7 max-w-xl relative">
                            <input
                                data-testid="search-input"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Buscar por nombre, SKU o marca…"
                                className="w-full bg-white/10 border border-white/20 focus:border-madera rounded-full outline-none py-3.5 pl-6 pr-14 text-white placeholder:text-white/50 transition-colors"
                            />
                            <button type="submit" data-testid="search-submit" className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-naranja hover:bg-naranja-dark flex items-center justify-center transition-colors">
                                <Search size={18} />
                            </button>
                        </form>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between py-4 border-b border-linea sticky top-16 md:top-20 bg-crema/95 backdrop-blur-md z-30">
                    <button data-testid="mobile-filter-toggle" onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 text-sm font-medium">
                        <SlidersHorizontal size={16} /> Filtros
                    </button>
                    <div className="hidden lg:block">
                        {(categoria || marca || search) && (
                            <button onClick={() => { setSearchInput(""); setParams(new URLSearchParams()); }} data-testid="clear-filters" className="flex items-center gap-1.5 text-sm text-mut hover:text-verde">
                                <X size={14} /> Limpiar filtros
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:block text-sm text-mut">Ordenar</span>
                        <select
                            data-testid="sort-select"
                            value={sort}
                            onChange={(e) => update({ sort: e.target.value })}
                            className="bg-hueso border border-linea rounded-full text-sm py-2.5 px-4 outline-none focus:border-madera"
                        >
                            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                    </div>
                </div>

                {/* Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex gap-10">
                    <aside className="hidden lg:block w-64 shrink-0">
                        <div className="sticky top-40 bg-hueso border border-linea rounded-2xl p-5"><FilterPanel /></div>
                    </aside>

                    <div className="flex-1 min-w-0">
                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                                {Array.from({ length: 12 }).map((_, i) => <div key={i} className="bg-secondary rounded-2xl animate-pulse aspect-[3/4]" />)}
                            </div>
                        ) : data.items.length === 0 ? (
                            <div className="text-center py-32">
                                <p className="font-display font-bold text-2xl text-ink">Sin resultados</p>
                                <p className="text-sm text-mut mt-2">Prueba con otra búsqueda o categoría.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                                {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
                            </div>
                        )}

                        {data.pages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-14" data-testid="pagination">
                                <button disabled={page <= 1} onClick={() => update({ page: String(page - 1) })} data-testid="page-prev" className="h-11 w-11 rounded-full flex items-center justify-center border border-verde text-verde disabled:opacity-25 hover:bg-verde hover:text-white transition-colors">
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-sm px-4 font-medium">{page} / {data.pages}</span>
                                <button disabled={page >= data.pages} onClick={() => update({ page: String(page + 1) })} data-testid="page-next" className="h-11 w-11 rounded-full flex items-center justify-center border border-verde text-verde disabled:opacity-25 hover:bg-verde hover:text-white transition-colors">
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="fixed inset-0 z-[70] lg:hidden">
                    <div className="absolute inset-0 bg-verde-900/60" onClick={() => setShowFilters(false)} />
                    <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85%] bg-crema p-6 overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <span className="font-display font-bold text-2xl">Filtros</span>
                            <button onClick={() => setShowFilters(false)}><X size={24} /></button>
                        </div>
                        <FilterPanel />
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
